import axios from "axios";
import { 
  getCommentsResponseMapper,
  getCasesMetricsMapper
} from "core/mappers/common";
import { API_ENDPOINT } from "core/utils/constants";
import { t } from "i18next";
import { convertUnixTimeToDate } from  "core/utils/dateTimeUtils";
import { Toast as showToast } from "core/components/global/toast/toast";
import { getIncidentsResponseMapper } from "../mappers/incidentsCardsMapper";
import { fetchBaseUrl } from "core/utils/common";

const apiURL = `${fetchBaseUrl({port: 8000})}/socai/api`;
const uiURL = `${fetchBaseUrl({port: 8090})}/socai/ui`;

function addMarkdownHyperlinks(input) {
  // Regular expression to find URLs in the string
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  
  // Replace URLs with Markdown-formatted hyperlinks
  const markdownString = input.replace(urlRegex, '[$1]($1)');
  
  return markdownString;
}

function tableUbaIdOutput(req_response) {
  const data = req_response.data;
    
  // Convert the comment postedOn from timestamp to a human readable date
  const comments = data["comments"].map(comment => ({
    ...comment,
    postedOn: convertUnixTimeToDate(comment.postedOn)
  }));

  const response = {
    'id': data["alert_uid"],
    'severity': data["severity"], // #special
    'status': data["status"],
    'tags': "tags" in data ? data["tags"] :  [],
    'comments': "comments" in data ? comments :  [],
    'assignedUsers':  [data["assignedto"]],
    'createdDate': convertUnixTimeToDate(data["createdDate"]), // #special
    'lastModified': convertUnixTimeToDate(data["lastModified"]),
    'ai_response_uba_risk_score': data["ai_response_uba_risk_score"],
    'employee_id': data["employee_id"],
    'case_information': data["case_information"]
  };

  return response;
}


function tableIrIdOutput(req_response) {
  const data = req_response.data;
    
  // Convert the comment postedOn from timestamp to a human readable date
  const comments = data["comments"].map(comment => ({
    ...comment,
    postedOn: convertUnixTimeToDate(comment.postedOn)
  }));

  const response = {
    'id': data["alert_uid"],
    'severity': data["severity"], // #special
    'status': data["status"],
    'tags': "tags" in data ? data["tags"] :  [],
    'comments': "comments" in data ? comments :  [],
    'assignedUsers':  [data["assignedto"]],
    'createdDate': convertUnixTimeToDate(data["createdDate"]), // #special
    'lastModified': convertUnixTimeToDate(data["lastModified"]),
    'ai_response_risk_score': data["ai_response_risk_score"],
    'employee_id': data["employee_id"],
    'case_information': data["case_information"]
  };

  return response;
}


function tableMcIdOutput(req_response) {
  const data = req_response.data;
    
  // Convert the comment postedOn from timestamp to a human readable date
  const comments = data["comments"].map(comment => ({
    ...comment,
    postedOn: convertUnixTimeToDate(comment.postedOn)
  }));
  
  const response = {
    'id': data["alert_id"],
    'severity': 0,
    'status': data["status"],
    'tags': "tags" in data ? data["tags"] :  [],
    'comments': "comments" in data ? comments :  [],
    'assignedUsers':  [data["assignedto"]],
    'createdDate': convertUnixTimeToDate(data["createdDate"]),
    'lastModified': convertUnixTimeToDate(data["lastModified"]),
    'event': data["event"],
    'event_summary': data["event_summary"],
    'file_analysis': data["file_analysis"],
    'slim_output_answers': data["slim_output_answers"],
    'completed_code_review': data["completed_code_review"],
    'code_analysis': data["code_analysis"],
    'code': data["code"]
  };

  return response;
}


function tableMuIdOutput(req_response) {
  const data = req_response.data;
    
  // Convert the comment postedOn from timestamp to a human readable date
  const comments = data["comments"].map(comment => ({
    ...comment,
    postedOn: convertUnixTimeToDate(comment.postedOn)
  }));
    
  const response = {
    'id': data["alert_id"],
    'severity': 0,
    'status': data["status"],
    'tags': "tags" in data ? data["tags"] :  [],
    'comments': "comments" in data ? comments :  [],
    'assignedUsers':  [data["assignedto"]],
    'createdDate': convertUnixTimeToDate(data["createdDate"]),
    'lastModified': convertUnixTimeToDate(data["lastModified"]),
    'event': data["event"],
    'code_analysis': data.code_analysis.replace(/\\n/g, "\n")
  };

  return response;
}


function tableFrontendIdOutput(req_response) {
  const data = req_response.data;
    
  // Convert the comment postedOn from timestamp to a human readable date
  const comments = data["comments"].map(comment => ({
    ...comment,
    postedOn: convertUnixTimeToDate(comment.postedOn)
  }));
  
  let cve = data["cve"];
  let mitre = data["mitre"];

  // Incorrect Markdown title syntax has been observed
  cve = cve && cve.includes("[Title: CVE Information]") ? cve.replace("[Title: CVE Information]", "###### CVE Information\n <br />") : cve;
  cve = cve && cve.includes("[CVE Information]") ? cve.replace("[CVE Information]", "###### CVE Information\n <br />") : cve;

  // Check for URLs and apply the correct formatting
  cve = cve ? addMarkdownHyperlinks(cve) : cve;
        
  // The title comes in various versions of the same, this should unify the known variations
  mitre = mitre && /MITRE(?:\sATT&CK)?\s?(M|m)apping/i.test(mitre)
  ? mitre.replace(/(MITRE(?:\sATT&CK)?\s?(M|m)apping)/gi, "")
  : mitre;

  if (mitre) {
    mitre = "\n\n ##### MITRE mapping\n" + mitre
  
    // Remove any HTML tags added to the data
    mitre = mitre && mitre.includes("<OCSF_LOG>") ? mitre.replace('<OCSF_LOG>', "") : mitre;
    mitre = mitre && mitre.includes("</OCSF_LOG>") ? mitre.replace('</OCSF_LOG>', "") : mitre;
    mitre = mitre && mitre.includes("<input>") ? mitre.replace('<input>', "") : mitre;
    mitre = mitre && mitre.includes("</input>") ? mitre.replace('</input>', "") : mitre;
    mitre = mitre && mitre.includes("<>") ? mitre.replace('<>', "") : mitre;
  
    // Check for URLs and apply the correct formatting
    mitre = mitre && mitre.includes("http") ? addMarkdownHyperlinks(mitre) : mitre;  
  }

  let icos = data["iocs"]


  // Build the response to feed the IncidentSummary component
  const response = {
      'id': data["findingId"],
      'severity': data["severity"],
      'status': data["status"],
      'classification': data["classification"],
      'organization': data["org"],
      'assignedUsers':  [data["assignedto"]],
      'description': data["summary"],
      'tags': "tags" in data ? data["tags"] :  [],
      'comments': "comments" in data ? comments :  [],
      'relatedIncidents': [],
      'createdDate': convertUnixTimeToDate(data["createdDate"]),
      'lastModified': convertUnixTimeToDate(data["lastModified"]),
      'flywheelReport': data["report"],
      'flywheelMitre': mitre,
      'flywheelIocs': icos,
      'flywheelCve': cve,
      'flywheelDetectionRule': data["detectionRules"],
      'flywheelConfigResponse': "",
      'flywheelConfigControls': {
        "response": "Response String to display",
        "nist": ["CA-3", "CM-6", "SI-41"],
        "CIS_Critical_Controls_v8": ['4.2', '12.3', '13.6'],
        "NIST_800_53_Rev5": ['CA-3', 'CM-6', 'SI-4'],
        "SOC_II": ['CC6.1', 'CC6.6', 'CC7.2'],
        "ISO_27001": ['A.13.1.1', 'A.13.1.2', 'A.13.1.3', 'A.13.2.1', 'A.13.2.2'],
        "ISMAP_Japan": ['13.1.1', '13.1.2', '13.1.3', '13.2.1', '13.2.2'],
        "HIPAA": ['No Control Mapping'],
        "NERC_CIP": ['CIP-005-7 R1', 'CIP-005-7 R2', 'CIP-005-7 R3', 'CIP-007-6 R1', 'CIP-007-6'],
        "CMM2": ["CM.L2-3.4.1", "CM.L2-3.4.2", "SL.L2-3.14.6", "SL.L2-3.14.7"],
        "NIST-800-171": ["3.4.1", "3.4.2", "3.14.6", "3.14.7"],
        "NIST_CSF": ['ID.AM-3', 'PR.IP-1', 'ID.RA-1', 'PR.DS-5', 'PR.IP-8', 'DE.AE-1', 'DE.AE-2'],
        "CCCS_Canada": ['CA-3', 'CM-6', 'SI-4'],
        "ISM_IRAP": ['ISM-0263', 'ISM-0639', 'ISM-1028', 'ISM-1030', 'ISM-1034', 'ISM-1192'],
        "NYDFS": ['500.14(a)(2)', '500.14(b)(1)'],
        "FFIEC": ['D3.DC. Im.B.1', 'D3.DC.Im.Int.1', 'D1.G.IT.B.13', 'DE.AE-1','D3.DC.Ev.E.1'],
        "CSA_CCM_V4": ['UEM-10', 'IVS-03']
      }
  }
  
  return response;
}

export const getIncidentDetailsByIdService = async (config, id: string, type: string) => {
  if (id.includes("/")) {
    id = id.replace(/\//g, "---");
  }

  // Fetch the incident by ID
  try {
    const req_response = await axios.request({
      method: 'GET',
      url: `${uiURL}/cases?id=${id}&type=${type}`,
      headers: config
    });
    
    if (req_response.status > 299) {
      return false;
    } else {
      let response;
      // Extract the response data, according to table we choose to query
      if (type == "main") {
        response = tableFrontendIdOutput(req_response);
      }
      else if (type == "awsfu") {
        response = tableIrIdOutput(req_response);
      }
      else if (type == "awsmc") {
        response = tableMcIdOutput(req_response);
      }
      else if (type == "awsmu") {
        response = tableMuIdOutput(req_response);
      }
      else if (type == "ubafu") {
        response = tableUbaIdOutput(req_response);
      }
      return response;
    }
  } catch (error) {
    console.error(`getIncidentDetailsByIdService error: ${JSON.stringify(error)}`)
    return false;
  }
}


/**
 * Allows a user to add a comment to an incident
 * @param comment 
 * @param id 
 * @returns 
 */
export const postUserComment = async (config, comment, id) => {
  if (id.includes("/")) {
    id = id.replace(/\//g, "---");
  }

  try {
    const req_response = await axios.request({
      method: 'POST',
      url: `${uiURL}/cases/${id}/comments`,
      headers: config,
      data : comment
    })

    if (req_response.status > 299) {
      // console.error(`Unable to add comments for incident ${id} with status ${req_response.status}`)
      // showToast(`Unable to add comments for incident ${id} with status ${req_response.status}`, "error");
    } else {
      return(req_response)
    }
  } catch (error) {
    // console.error(`postUserComment error: ${JSON.stringify(error)}`)
    // showToast(`Unable to add comments for incident ${id}`, "error");
  }
}


/**
 * Retrieves all comments for an incident
 * @param comment 
 * @param id 
 * @returns 
 */
export const getIncidentComments = async (config, id) => {
  if (id.includes("/")) {
    id = id.replace(/\//g, "---");
  }
  
  try {
    const req_response = await axios.request({
      method: 'GET',
      url: `${uiURL}/cases/${id}/comments`,
      headers: config
    })

    if (req_response.status > 299) {
      // console.error(`Unable to fetch comments for incident ${id} with status ${req_response.status}`)
      // showToast(`Unable to fetch comments for incident ${id} with status ${req_response.status}`, "error");
    } else {
      const data = await req_response.data;
      return getCommentsResponseMapper(t, data);
    }
  } catch (error) {
    // console.error(`getIncidentComments error: ${JSON.stringify(error)}`)
  }
}


/**
 * Below this point are all the original user functions but we are not currently
 * using many of the mechanics, e.g. companies or organizations plural
 */
export async function getIncidentsRequestRecursion(config, pageSize, offset, newestStateEventTs = 0) {
  let url = `${uiURL}/cases`;
  url += `?size=${pageSize}`;
  url += `&offset=${offset}`;
  url += `&newestStateEventTs=${newestStateEventTs}`;

  try {
    const response = await axios.request({
      method: 'GET',
      url: url,
      headers: config,
    });
    
    if (response.status > 299) {
      // console.error(`Error fetching records from offset ${offset} and after timestamp ${newestStateEventTs}`);
      // showToast(`Error fetching records from offset ${offset} and after timestamp ${newestStateEventTs}`, "error");
    } else {
      return response;
    }
  } catch (error) {
    // console.error(`getIncidentsRequestRecursion error: ${JSON.stringify(error)}`)
    // showToast(`Unable to fetch records from offset ${offset} and after timestamp ${newestStateEventTs}`, "error");
  }
}


export async function getIncidentsUpdatesRequestRecursion(config, lastUpdatedTs = 0) {
  let url = `${uiURL}/cases`;
  url += `?lastUpdatedTs=${lastUpdatedTs}`;

  const response = await axios.request({
    method: 'GET',
    url: url,
    headers: config,
  });

  return response;
}

/**
 * Allows a user to assign themselves or another to an incident
 * @param email 
 * @returns 
 */
export const assignIncidentRequestService = async (email, id, config) => {
  if (id.includes("/")) {
    id = id.replace(/\//g, "---");
  }

  const response = await axios.request({
    method: 'PUT',
    url: `${uiURL}/cases/${id}/assigned`,
    headers: config,
    data: { "email": email }
  });
  
  return response;
};

export const setIncidentStatusRequestService = async (status, id, config) => {
  if (id.includes("/")) {
    id = id.replace(/\//g, "---");
  }

  const response = await axios.request({
    method: 'PUT',
    url: `${uiURL}/cases/${id}/status`,
    headers: config,
    data: { "status": status }
  });
  
  return response;
};

/**
 * Retrieves all comments for an incident
 * @param comment 
 * @param id 
 * @returns 
 */
export const getIncidentFilters = async (config) => {
  try {
    const req_response = await axios.request({
      method: 'GET',
      url: `${uiURL}/cases/filters`,
      headers: config
    })

    if (req_response.status > 299) {
      // showToast(`Unable to fetch the filtered incidents`, "error");
    } else {
      const data = await req_response.data;
      return(data);
    }
  } catch (error) {
    // showToast(`Unable to fetch the filtered incidents`, "error");
    return null;
  }
}


/**
 * Retrieves all comments for an incident
 * @param comment 
 * @param id 
 * @returns 
 */
export const getHomeMetrics = async (config) => {
  try {
    const req_response = await axios.request({
      method: 'GET',
      url: `${uiURL}/cases/metrics/home`,
      headers: config
    })

    if (req_response.status > 299) {
      // console.error(`getHomeMetrics error: ${JSON.stringify(error)}`)
      // showToast(`Unable to fetch incident metrics`, "error");
    } else {
      const data = await req_response.data;
      return(data);
    }
  } catch (error) {
    // console.error(`getHomeMetrics error: ${JSON.stringify(error)}`)
    // showToast(`Unable to fetch incident metrics`, "error");
  }
}

/**
 * Retrieves all comments for an incident
 * @param comment 
 * @param id 
 * @returns 
 */
export const getCasesMetrics = async (config) => {
  try {
    const req_response = await axios.request({
      method: 'GET',
      url: `${uiURL}/cases/metrics/cases`,
      headers: config
    })

    if (req_response.status > 299) {
      // console.error(`Unable to fetch metrics for cases`)
      // showToast(`Unable to fetch metrics for cases`, "error");
      // showToast(`Unable to fetch incident metrics`, "error");
    } else {
      const data = await req_response.data;
      return(getCasesMetricsMapper(t, data));
    }
  } catch (error) {
    // console.error(`getIncidentsByPage error: ${JSON.stringify(error)}`)
    // showToast(`Unable to fetch incident metrics`, "error");
  }
}

/**
 * Retrieves all comments for an incident
 * @param comment 
 * @param id 
 * @returns 
 */
export const getIncidentsByPage = async (config, size, page, order, orderBy, filters, search) => {
  try {
    const merged_params = {...filters, ...{
      'size': size,
      'page': page,
      'order': order,
      'orderBy': orderBy
    }}

    if (search !== "") {
      merged_params["search"] = search
    }

    const req_response = await axios.request({
      method: 'GET',
      url: `${uiURL}/cases`,
      headers: config,
      params: merged_params
    })

    if (req_response.status > 299) {
      return req_response;
    } else {
      const data = await req_response;
      return data;
    }
  } catch (error) {
    return null;
  }
}


/**
 * Gets all incidents assigned to a user
 * @param email 
 * @returns 
 */
export const getIncidentsByEmailServiceApi = async (config, email: string) => {
  try {
    const req_response = await axios.request({
      method: 'GET',
      url: `${uiURL}/${API_ENDPOINT.IDM_USERS}/${email}/cases`,
      headers: config
    });

    if (req_response.status > 299) {
      // console.error(`Unable to fetch incidents for ${email}`);
      // showToast(`Unable to fetch incidents for ${email}`, "error");
      return false;
    } else {
      const data = await req_response.data;
      return getIncidentsResponseMapper(t, data);
    } 
  } catch (error) {
    // console.error(`getIncidentsByEmailServiceApi error: ${JSON.stringify(error)}`)
    // showToast(`Unable to fetch incidents for ${email}`, "error");
    return false;
  }
}

export const fetchAIAnalysisData = async (userInput, id, task) => {
  try {
    const req_response = await axios.request({
      method: 'POST',
      url: `${apiURL}/task/${task}`,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'x-api-key': 'dev-key'
      },
      data : { "conversation_id": id, "user_input": userInput }
    })

    if (req_response.status > 299) {
      // console.error(`Unable to add comments for incident ${id} with status ${req_response.status}`)
      // showToast(`Unable to get similar incident`, "error");
    } else {
      return(req_response.data.response)
    }
  } catch (error) {
    
    // showToast(`Unable to get similar incident`, "error");
  }
}
export const fetchAIAnalysisDataStream = async (
  userInput: string,
  id: string,
  task: string,
  onChunk: (chunk: string) => void
) => {
  const response = await fetch(
    `https://dev.secaiasst.com/socai/api/task/${task}?stream=true`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'x-api-key': 'dev-key'
      },
      body: JSON.stringify({ conversation_id: id, user_input: userInput })
    }
  );

  if (!response.body) throw new Error('No response body');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let done = false;

  while (!done) {
    const { value, done: doneReading } = await reader.read();
    done = doneReading;
    if (value) {
      const chunk = decoder.decode(value, { stream: true });
      onChunk(chunk);
    }
  }
};
